import React from 'react';
import bemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import './DocumentContentEditorProductTable.scss';
import { Row, Text, Toggle, Headline, Column, Input, Select } from '@upsales/components';
import { DocumentTemplateState } from './DocumentTemplateEditor';
import { getValueHash, onEditorChange } from './DocumentTemplateEditorDesign';
import { getIndicesFromColId } from './DocumentContentEditorProductTable';
import { useFeatureAvailable } from 'App/components/hooks';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { usePopupPortalAnchorsContext } from 'App/helpers/PopupPortalAnchors';

type Props = {
	state: DocumentTemplateState;
	dispatch: React.Dispatch<Partial<DocumentTemplateState>>;
};

export const DocumentContentEditorProductTableAdditional = ({ state, dispatch }: Props) => {
	const { t } = useTranslation();
	const { anchor } = usePopupPortalAnchorsContext();
	const hasProductDescription = useFeatureAvailable(Feature.PRODUCT_DESCRIPTION);
	const lang = {
		additionalSettings: t('admin.documentTemplateEditor.productTable.additionalSettings'),
		discounts: t('admin.documentTemplateEditor.productTable.discounts'),
		priceSummation: t('admin.documentTemplateEditor.productTable.priceSummation'),
		productDescription: t('default.productDescription'),
		productDescriptionDescription: t('admin.documentTemplateEditor.productTable.productDescription.description'),
		totalRecurringPrice: t('admin.documentTemplateEditor.productTable.totalPriceRecurring'),
		totalOneOffPrice: t('admin.documentTemplateEditor.productTable.totalPriceOneOff'),
		totalPrice: t('admin.documentTemplateEditor.productTable.totalPrice'),
		totalRecurringPriceDefaultValue: t(
			'admin.documentTemplateEditor.productTable.totalPriceRecurring.defaultValue'
		),
		totalOneOffPriceDefaultValue: t('admin.documentTemplateEditor.productTable.totalPriceOneOff.defaultValue'),
		totalPriceDefaultValue: t('admin.documentTemplateEditor.productTable.totalPrice.defaultValue'),
		vat: t('admin.documentTemplateEditor.productTable.vat'),
		vatRate: t('admin.documentTemplateEditor.productTable.vatRate'),
		vatDisplay: t('admin.documentTemplateEditor.productTable.vatDisplay'),
		vatDisplayExcl: t('admin.documentTemplateEditor.productTable.vatDisplay.excl'),
		vatDisplayIncl: t('admin.documentTemplateEditor.productTable.vatDisplay.incl'),
		vatDisplayBoth: t('admin.documentTemplateEditor.productTable.vatDisplay.both'),
		vatRowDisplay: t('admin.documentTemplateEditor.productTable.vatRowDisplay')
	};

	const classes = new bemClass('DocumentContentEditorProductTable');

	const enum Settings {
		PriceSummation,
		ProductDescription,
		Discounts,
		Vat
	}

	const PriceType = {
		Recurring: 'recurring',
		OneOff: 'oneOff',
		Total: 'total'
	} as const;

	const onToggleSetting = (type: Settings) => {
		const [rowIndex, colIndex] = getIndicesFromColId(state);
		if (rowIndex === undefined || colIndex === undefined) return;
		if (rowIndex === -1 || colIndex === -1) {
			return;
		}
		const config = { ...state.config };
		const column = state.selectedColumn;
		if (!column) return;
		switch (type) {
			case Settings.PriceSummation:
				column.showPriceSummation = !column.showPriceSummation;
				if (!column.showPriceSummation && column.ProductTableTotalPrice) {
					column.ProductTableTotalPrice.recurring = lang.totalRecurringPriceDefaultValue;
					column.ProductTableTotalPrice.oneOff = lang.totalOneOffPriceDefaultValue;
					column.ProductTableTotalPrice.total = lang.totalPriceDefaultValue;
				}
				break;
			case Settings.ProductDescription:
				column.showProductDescription = !column.showProductDescription;
				break;
			case Settings.Discounts:
				column.showDiscounts = !column.showDiscounts;
				break;
			case Settings.Vat:
				if (column.vatRate) {
					column.vatRate = undefined;
					column.vatDisplay = undefined;
					column.vatRowDisplay = undefined;
				} else {
					column.vatRate = 0.25;
					column.vatDisplay = 'incl';
				}
				break;
		}
		config.rows[rowIndex].columns[colIndex] = column;
		dispatch({
			config: config,
			hash: getValueHash(config),
			selectedColumn: column
		});
		onEditorChange(state, dispatch, { config });
	};

	const onVatFieldChange = (field: 'vatRate' | 'vatDisplay' | 'vatRowDisplay', value: number | string) => {
		const [rowIndex, colIndex] = getIndicesFromColId(state);
		if (rowIndex === undefined || colIndex === undefined) return;
		if (rowIndex === -1 || colIndex === -1) return;
		const config = { ...state.config };
		const column = state.selectedColumn;
		if (!column) return;
		if (field === 'vatRate') {
			column.vatRate = value as number;
		} else if (field === 'vatRowDisplay') {
			column.vatRowDisplay = value as 'excl' | 'incl' | 'both';
		} else {
			column.vatDisplay = value as 'excl' | 'incl' | 'both';
		}
		config.rows[rowIndex].columns[colIndex] = column;
		dispatch({
			config: config,
			hash: getValueHash(config),
			selectedColumn: column
		});
		onEditorChange(state, dispatch, { config });
	};

	const onFieldChange = (type: (typeof PriceType)[keyof typeof PriceType], value: string) => {
		const [rowIndex, colIndex] = getIndicesFromColId(state);
		if (rowIndex === undefined || colIndex === undefined) return;
		if (rowIndex === -1 || colIndex === -1) {
			return;
		}
		const config = { ...state.config };
		const column = state.selectedColumn;
		if (!column?.ProductTableTotalPrice) return;
		switch (type) {
			case PriceType.Recurring:
				column.ProductTableTotalPrice.recurring = value;
				break;
			case PriceType.OneOff:
				column.ProductTableTotalPrice.oneOff = value;
				break;
			case PriceType.Total:
				column.ProductTableTotalPrice.total = value;
				break;
		}
		config.rows[rowIndex].columns[colIndex] = column;
		dispatch({
			config: config,
			hash: getValueHash(config),
			selectedColumn: column
		});
		onEditorChange(state, dispatch, { config });
	};

	return (
		<div className={classes.b()}>
			<Headline size={'xs'}>{lang.additionalSettings}</Headline>
			<Row className={classes.elem('additional-setting').b()}>
				<Column>
					<Text>{lang.priceSummation}</Text>
				</Column>
				<Column className={classes.elem('toggle').b()} data-testid="price-summation-toggle">
					<Toggle
						checked={state.selectedColumn ? state.selectedColumn.showPriceSummation : false}
						onChange={() => onToggleSetting(Settings.PriceSummation)}
					/>
				</Column>
			</Row>
			{state.selectedColumn && state.selectedColumn.showPriceSummation ? (
				<Row className={classes.elem('additional-setting').mod('text-input').b()}>
					<Column>
						<Text>{lang.totalRecurringPrice}</Text>
					</Column>
					<Column>
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onFieldChange(PriceType.Recurring, e.target.value)
							}
							value={state.selectedColumn.ProductTableTotalPrice?.recurring || ''}
						></Input>
					</Column>
				</Row>
			) : null}
			{state.selectedColumn && state.selectedColumn.showPriceSummation ? (
				<Row className={classes.elem('additional-setting').mod('text-input').b()}>
					<Column>
						<Text>{lang.totalOneOffPrice}</Text>
					</Column>
					<Column>
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onFieldChange(PriceType.OneOff, e.target.value)
							}
							value={state.selectedColumn.ProductTableTotalPrice?.oneOff || ''}
						></Input>
					</Column>
				</Row>
			) : null}
			{state.selectedColumn && state.selectedColumn.showPriceSummation ? (
				<Row className={classes.elem('additional-setting').mod('text-input').b()}>
					<Column>
						<Text>{lang.totalPrice}</Text>
					</Column>
					<Column>
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onFieldChange(PriceType.Total, e.target.value)
							}
							value={state.selectedColumn.ProductTableTotalPrice?.total || ''}
						></Input>
					</Column>
				</Row>
			) : null}
			{hasProductDescription ? (
				<Row className={classes.elem('additional-setting').b()}>
					<Column>
						<Text>{lang.productDescription}</Text>
						<Text size="sm" className={classes.elem('description').b()}>
							{lang.productDescriptionDescription}
						</Text>
					</Column>
					<Column className={classes.elem('toggle').b()}>
						<Toggle
							data-testid="product-description-toggle"
							checked={state.selectedColumn ? state.selectedColumn.showProductDescription : false}
							onChange={() => onToggleSetting(Settings.ProductDescription)}
						/>
					</Column>
				</Row>
			) : null}
			<Row className={classes.elem('additional-setting').b()}>
				<Column>
					<Text>{lang.discounts}</Text>
				</Column>
				<Column className={classes.elem('toggle').b()}>
					<Toggle
						data-testid="discounts-toggle"
						checked={state.selectedColumn ? state.selectedColumn.showDiscounts : false}
						onChange={() => onToggleSetting(Settings.Discounts)}
					/>
				</Column>
			</Row>
			<Row className={classes.elem('additional-setting').b()}>
				<Column>
					<Text>{lang.vat}</Text>
				</Column>
				<Column className={classes.elem('toggle').b()}>
					<Toggle
						data-testid="vat-toggle"
						checked={!!state.selectedColumn?.vatRate}
						onChange={() => onToggleSetting(Settings.Vat)}
					/>
				</Column>
			</Row>
			{state.selectedColumn?.vatRate ? (
				<>
					<Row className={classes.elem('additional-setting').mod('text-input').b()}>
						<Column>
							<Text>{lang.vatRate}</Text>
						</Column>
						<Column>
							<Select
								data-testid="vat-rate-select"
								anchor={anchor}
								options={[
									{ id: 0.06, title: '6%' },
									{ id: 0.12, title: '12%' },
									{ id: 0.25, title: '25%' }
								]}
								value={{ id: state.selectedColumn.vatRate, title: `${state.selectedColumn.vatRate * 100}%` }}
								onChange={(val: { id: number }) => onVatFieldChange('vatRate', val.id)}
							/>
						</Column>
					</Row>
					<Row className={classes.elem('additional-setting').mod('text-input').b()}>
						<Column>
							<Text>{lang.vatDisplay}</Text>
						</Column>
						<Column>
							<Select
								data-testid="vat-display-select"
								anchor={anchor}
								options={[
									{ id: 'incl', title: lang.vatDisplayIncl },
									{ id: 'both', title: lang.vatDisplayBoth }
								]}
								value={{ id: state.selectedColumn.vatDisplay || 'incl', title: state.selectedColumn.vatDisplay === 'both' ? lang.vatDisplayBoth : lang.vatDisplayIncl }}
								onChange={(val: { id: string }) => onVatFieldChange('vatDisplay', val.id)}
							/>
						</Column>
					</Row>
					{state.selectedColumn.vatDisplay === 'both' ? (
						<Row className={classes.elem('additional-setting').mod('text-input').b()}>
							<Column>
								<Text>{lang.vatRowDisplay}</Text>
							</Column>
							<Column>
								<Select
									data-testid="vat-row-display-select"
									anchor={anchor}
									options={[
										{ id: 'excl', title: lang.vatDisplayExcl },
										{ id: 'incl', title: lang.vatDisplayIncl },
										{ id: 'both', title: lang.vatDisplayBoth }
									]}
									value={(() => {
									const val = state.selectedColumn.vatRowDisplay || 'excl';
									const titleMap = { excl: lang.vatDisplayExcl, incl: lang.vatDisplayIncl, both: lang.vatDisplayBoth };
									return { id: val, title: titleMap[val] };
								})()}
									onChange={(val: { id: string }) => onVatFieldChange('vatRowDisplay', val.id)}
								/>
							</Column>
						</Row>
					) : null}
				</>
			) : null}
		</div>
	);
};
